import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

const VIENNA_TZ = "Europe/Vienna";

export const INFINITY_DATE_LABEL = "∞";

export function isMaxDate(date: string | number | Date): boolean {
    const d = new Date(date);
    if (isNaN(d.getTime())) return false;
    return d.getUTCFullYear() >= 3333;
}

export function fromViennaToUtc(date: string | number | Date) {
    return fromZonedTime(date, VIENNA_TZ);
}

export function fromUtcToVienna(date: string | number | Date) {
    return toZonedTime(date, VIENNA_TZ);
}

export function createViennaDate(
    year: number,
    month: number,
    day: number,
): Date {
    const viennaDate = new Date(year, month, day, 0, 0, 0, 0);
    return fromViennaToUtc(viennaDate);
}

export function nowInVienna(): Date {
    return fromUtcToVienna(new Date());
}

export function formatViennaDate(
    utcDate: Date | string,
    opts: Intl.DateTimeFormatOptions = {},
): string {
    if (isMaxDate(utcDate)) return INFINITY_DATE_LABEL;
    return new Date(utcDate).toLocaleDateString(undefined, {
        ...opts,
        timeZone: VIENNA_TZ,
    });
}

export function formatViennaDateOnly(utcDate: Date): string {
    const v = fromUtcToVienna(utcDate);
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export function formatViennaDateTime(
    utcDate: Date | string,
    opts: Intl.DateTimeFormatOptions = {},
): string {
    if (isMaxDate(utcDate)) return INFINITY_DATE_LABEL;
    return new Date(utcDate).toLocaleString(undefined, {
        ...opts,
        timeZone: VIENNA_TZ,
    });
}

export function formatViennaTime(
    utcDate: Date | string,
    opts: Intl.DateTimeFormatOptions = {},
): string {
    if (isMaxDate(utcDate)) return INFINITY_DATE_LABEL;
    return new Date(utcDate).toLocaleTimeString(undefined, {
        ...opts,
        timeZone: VIENNA_TZ,
    });
}

export function normalizeToViennaDay(date: Date): Date {
    const viennaDate = fromUtcToVienna(date);
    return createViennaDate(
        viennaDate.getFullYear(),
        viennaDate.getMonth(),
        viennaDate.getDate(),
    );
}

export function addDaysVienna(date: Date, days: number): Date {
    const viennaDate = fromUtcToVienna(date);
    const newDate = new Date(
        viennaDate.getFullYear(),
        viennaDate.getMonth(),
        viennaDate.getDate() + days,
    );
    return createViennaDate(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
    );
}

export function lastDayOfMonthVienna(year: number, month: number): Date {
    const nextMonthFirst = createViennaDate(year, month + 1, 1);
    return addDaysVienna(nextMonthFirst, -1);
}

export function formatViennaDistanceToNow(date: string | number | Date) {
    return formatDistanceToNow(fromUtcToVienna(date), {
        addSuffix: true,
        locale: de,
    });
}
